export type PolygonPoint = { x: number; y: number };

export type PolygonPlacement = {
  point: PolygonPoint;
  clearance: number;
  meetsMinimumClearance: boolean;
};

type PlacementOptions = {
  /** Half wall thickness per polygon edge, in the same units as the coordinates. */
  wallInsets?: readonly number[];
  minimumClearance?: number;
  precision?: number;
  preferredPoint?: PolygonPoint;
};

type Edge = { start: PolygonPoint; end: PolygonPoint; inset: number };
type Sample = { point: PolygonPoint; clearance: number; inside: boolean };
type Cell = Sample & { halfWidth: number; halfHeight: number; upperBound: number };

/** Tests the actual polygon, including its boundary, independently of ring winding. */
export function polygonContainsPoint(polygon: readonly PolygonPoint[], point: PolygonPoint, includeBoundary = true): boolean {
  if (!Number.isFinite(point.x) || !Number.isFinite(point.y)) return false;
  const edges = polygonEdges(polygon);
  if (edges.length < 3) return false;
  const clearance = samplePoint(point, edges).clearance;
  return includeBoundary ? clearance >= -1e-9 : clearance > 1e-9;
}

/** Finds the best interior anchor of a simple polygon, even when the requested clearance cannot fit.
 * Uses a polylabel-style branch-and-bound search. Coordinates, insets and precision use model units.
 * Open/closed rings and either winding are accepted; degenerate polygons return undefined.
 */
export function findPolygonPlacement(
  polygon: readonly PolygonPoint[],
  options: PlacementOptions = {},
): PolygonPlacement | undefined {
  const edges = polygonEdges(polygon, options.wallInsets);
  if (edges.length < 3) return undefined;
  const seed = interiorSeed(edges);
  if (!seed) return undefined;

  const precision = Number.isFinite(options.precision) && options.precision! > 0 ? options.precision! : 0.01;
  const minimumClearance = options.minimumClearance ?? 0.3;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const edge of edges) {
    minX = Math.min(minX, edge.start.x);
    minY = Math.min(minY, edge.start.y);
    maxX = Math.max(maxX, edge.start.x);
    maxY = Math.max(maxY, edge.start.y);
  }

  const center = { x: (minX + maxX) / 2, y: (minY + maxY) / 2 };
  const preferred = samplePoint(options.preferredPoint ?? center, edges);
  let best = samplePoint(seed, edges);
  const initialCell = createCell(center, (maxX - minX) / 2, (maxY - minY) / 2, edges);
  if (initialCell.inside && initialCell.clearance > best.clearance) best = initialCell;
  if (preferred.inside && preferred.clearance >= best.clearance) best = preferred;

  const queue: Cell[] = [];
  pushCell(queue, initialCell);
  while (queue.length > 0) {
    const cell = popCell(queue);
    if (cell.inside && cell.clearance > best.clearance) best = cell;
    if (cell.upperBound <= best.clearance + precision) continue;

    // Split the longer axis so elongated corridors do not require a large initial grid.
    const splitX = cell.halfWidth >= cell.halfHeight;
    const halfWidth = splitX ? cell.halfWidth / 2 : cell.halfWidth;
    const halfHeight = splitX ? cell.halfHeight : cell.halfHeight / 2;
    for (const direction of [-1, 1]) {
      const point = {
        x: cell.point.x + (splitX ? direction * halfWidth : 0),
        y: cell.point.y + (splitX ? 0 : direction * halfHeight),
      };
      pushCell(queue, createCell(point, halfWidth, halfHeight, edges));
    }
  }

  // Preserve a previous position when it is still equally good within the search precision.
  if (
    preferred.inside &&
    preferred.clearance >= best.clearance - precision &&
    (preferred.clearance >= minimumClearance || best.clearance < minimumClearance)
  )
    best = preferred;

  return {
    point: { ...best.point },
    clearance: best.clearance,
    meetsMinimumClearance: best.clearance >= minimumClearance,
  };
}

/** Builds finite, nonzero edges while preserving the caller's wall-to-edge indexing. */
function polygonEdges(polygon: readonly PolygonPoint[], insets: readonly number[] = []): Edge[] {
  const edges: Edge[] = [];
  for (let index = 0; index < polygon.length; index++) {
    const start = polygon[index];
    const end = polygon[(index + 1) % polygon.length];
    if (![start.x, start.y, end.x, end.y].every(Number.isFinite)) return [];
    if (start.x === end.x && start.y === end.y) continue;
    const inset = Number.isFinite(insets[index]) ? Math.max(0, insets[index]) : 0;
    edges.push({ start, end, inset });
  }
  return edges;
}

/** Measures clearance to wall faces while keeping the polygon boundary an absolute constraint. */
function samplePoint(point: PolygonPoint, edges: readonly Edge[]): Sample {
  let inside = false;
  let boundaryDistance = Infinity;
  let wallDistance = Infinity;
  for (const { start, end, inset } of edges) {
    if (start.y > point.y !== end.y > point.y) {
      const crossingX = start.x + ((point.y - start.y) * (end.x - start.x)) / (end.y - start.y);
      if (point.x < crossingX) inside = !inside;
    }
    const distance = distanceToSegment(point, start, end);
    boundaryDistance = Math.min(boundaryDistance, distance);
    wallDistance = Math.min(wallDistance, distance - inset);
  }
  const signedDistance = inside ? boundaryDistance : -boundaryDistance;
  return { point, inside: inside && boundaryDistance > 0, clearance: Math.min(signedDistance, wallDistance) };
}

/** Computes the shortest Euclidean distance to a finite segment, including its endpoints. */
function distanceToSegment(point: PolygonPoint, start: PolygonPoint, end: PolygonPoint): number {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const projection = ((point.x - start.x) * dx + (point.y - start.y) * dy) / (dx * dx + dy * dy);
  const offset = Math.max(0, Math.min(1, projection));
  return Math.hypot(point.x - start.x - offset * dx, point.y - start.y - offset * dy);
}

/** Seeds the search strictly inside, even for polygons much narrower than the requested precision. */
function interiorSeed(edges: readonly Edge[]): PolygonPoint | undefined {
  const heights = [...new Set(edges.map((edge) => edge.start.y))].sort((a, b) => a - b);
  for (let index = 1; index < heights.length; index++) {
    const y = (heights[index - 1] + heights[index]) / 2;
    const intersections: number[] = [];
    for (const { start, end } of edges) {
      if (start.y > y === end.y > y) continue;
      intersections.push(start.x + ((y - start.y) * (end.x - start.x)) / (end.y - start.y));
    }
    intersections.sort((a, b) => a - b);
    for (let pair = 0; pair + 1 < intersections.length; pair += 2) {
      const point = { x: (intersections[pair] + intersections[pair + 1]) / 2, y };
      if (samplePoint(point, edges).inside) return point;
    }
  }
  return undefined;
}

/** Bounds the best possible clearance anywhere inside a search cell using its half diagonal. */
function createCell(point: PolygonPoint, halfWidth: number, halfHeight: number, edges: readonly Edge[]): Cell {
  const sample = samplePoint(point, edges);
  return { ...sample, halfWidth, halfHeight, upperBound: sample.clearance + Math.hypot(halfWidth, halfHeight) };
}

/** Adds a cell to a max heap so the most promising region is examined first. */
function pushCell(queue: Cell[], cell: Cell): void {
  let index = queue.length;
  queue.push(cell);
  while (index > 0) {
    const parent = Math.floor((index - 1) / 2);
    if (queue[parent].upperBound >= cell.upperBound) break;
    queue[index] = queue[parent];
    index = parent;
  }
  queue[index] = cell;
}

/** Removes the most promising cell and restores the heap ordering. */
function popCell(queue: Cell[]): Cell {
  const first = queue[0];
  const last = queue.pop()!;
  if (queue.length === 0) return first;
  let index = 0;
  while (index * 2 + 1 < queue.length) {
    let child = index * 2 + 1;
    if (child + 1 < queue.length && queue[child + 1].upperBound > queue[child].upperBound) child++;
    if (last.upperBound >= queue[child].upperBound) break;
    queue[index] = queue[child];
    index = child;
  }
  queue[index] = last;
  return first;
}
